import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { useNotifications } from '@sequencehq/core-components'
import {
  downloadBlob,
  filenameFromResponse,
  getPortalDomain
} from '@sequencehq/utils'
import { useCallback } from 'react'
import { match } from 'ts-pattern'
import { pdf } from '@react-pdf/renderer'
import { PdfInvoiceRenderer } from '@sequencehq/invoice-content'
import { getEnvironment, isProductionEnv } from 'lib/environment/environment'
import { toInvoiceRendererData } from 'Invoices/components/CreateEditShared'
import {
  fetchAggregatedInvoice,
  getHumanReadableInvoiceNumber
} from 'Invoices/utils'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

type InvoiceStatus = Dashboardv99990101Api.Invoices.InvoiceStatus
type Props = {
  invoiceId: string
  invoiceNumber: string | undefined
  invoiceStatus: InvoiceStatus
  /** Overrides the default download file name. Include the extension. */
  fileName?: string
}

export const useDownloadInvoicePdf = () => {
  const { displayPromiseNotification } = useNotifications()
  const { showServicePeriodsAlways, usePdfInvoiceAlternativeHeader } =
    useFeatureFlags()

  const getClientSideBlob = useCallback(
    async (invoiceId: string) => {
      const invoice = await fetchAggregatedInvoice(invoiceId)

      const merchantData =
        await dashboard20240730Client.getMerchantForSequenceAccount()

      if (!merchantData.data) {
        throw new Error('Unable to fetch invoice data')
      }

      const accountSettingsData =
        await dashboard20240730Client.getSequenceAccountSettings()

      const invoiceMerchantData =
        await dashboard20240730Client.getInvoiceMerchantDetails({
          currency: invoice.currency ?? 'USD',
          country:
            invoice.customerBillingAddress?.country ??
            merchantData.data.address.country,
          state: invoice.customerBillingAddress?.state
        })

      const currencyConversions =
        await dashboard20240730Client.getInvoiceCurrencyConversions({
          id: invoiceId
        })

      const dueDateSettings =
        await dashboardv20240509Client.getInvoiceAccountingDateSettings()

      if (
        !accountSettingsData.data ||
        !invoiceMerchantData.data ||
        !dueDateSettings.data
      ) {
        throw new Error('Unable to fetch invoice data')
      }

      const customerPortalEnabled = merchantData.data.customerPortalEnabled
      const portalDomain = getPortalDomain(getEnvironment())

      const rendererData = toInvoiceRendererData({
        clientInvoice: invoice,
        defaultCurrency: accountSettingsData.data.enabledCurrencies.at(0),
        merchantData: merchantData.data,
        invoiceMerchantData: invoiceMerchantData.data,
        currencyConversions: currencyConversions.data?.items ?? [],
        invoiceDueDateCalculatedFrom:
          dueDateSettings.data.invoiceDueDateCalculatedFrom,
        alwaysShowServicePeriod: showServicePeriodsAlways,
        usePdfInvoiceAlternativeHeader,
        // PDFs are customer-facing, so credit notes link to the customer
        // portal — mirroring the invoice's urlViewOnline on the static path.
        buildCreditNoteUrl: creditNoteId =>
          customerPortalEnabled && invoice.customerId
            ? `${portalDomain}/customers/${invoice.customerId}/credit-notes/${creditNoteId}`
            : undefined
      })

      const blob = await pdf(
        <PdfInvoiceRenderer
          data={rendererData}
          theme="DEFAULT"
          isProductionEnv={isProductionEnv()}
        />
      ).toBlob()

      return blob
    },
    [showServicePeriodsAlways, usePdfInvoiceAlternativeHeader]
  )

  const getServerSidePdf = useCallback(async (invoiceId: string) => {
    const invoicePdfRes = await dashboard20240730Client.getInvoicePdf({
      id: invoiceId
    })

    const blob = invoicePdfRes.data

    if (!blob) {
      throw new Error('An error occurred while downloading the invoice')
    }

    return { blob, apiFileName: filenameFromResponse(invoicePdfRes.res, '') }
  }, [])

  const getInvoicePdf = useCallback(
    async ({
      invoiceId,
      invoiceStatus
    }: {
      invoiceId: string
      invoiceStatus: InvoiceStatus
    }): Promise<{ blob: Blob; apiFileName: string }> => {
      return match(invoiceStatus)
        .with('DRAFT', 'IN_PROGRESS', 'FINAL', async () => ({
          blob: await getClientSideBlob(invoiceId),
          apiFileName: ''
        }))
        .with('SENT', 'VOIDED', () => getServerSidePdf(invoiceId))
        .exhaustive()
    },
    [getClientSideBlob, getServerSidePdf]
  )

  const downloadPdf = useCallback(
    async ({ invoiceId, invoiceNumber, invoiceStatus, fileName }: Props) => {
      await displayPromiseNotification(
        (async () => {
          const { blob, apiFileName } = await getInvoicePdf({
            invoiceId,
            invoiceStatus
          })

          const downloadFileName =
            fileName ||
            apiFileName ||
            `${getHumanReadableInvoiceNumber({
              invoiceNumber,
              invoiceStatus
            })}.pdf`

          downloadBlob(downloadFileName)(blob)
        })(),
        {
          loading: 'Preparing download',
          success: 'Download ready',
          error: 'PDF download failed'
        }
      )
    },
    [displayPromiseNotification, getInvoicePdf]
  )

  const fetchBlob = useCallback(
    async ({
      invoiceId,
      invoiceStatus
    }: {
      invoiceId: string
      invoiceStatus: InvoiceStatus
    }) => {
      const { blob } = await getInvoicePdf({ invoiceId, invoiceStatus })
      return blob
    },
    [getInvoicePdf]
  )

  return { downloadPdf, fetchBlob }
}
